import * as React from "react";
import { CssBaseline, Box } from "@mui/material";

import { LinkToCinemaSelectPage } from "../components/TextLinks/LinkToCinemaSelectPage.tsx";
import { LandingPageDescriptionBoxes } from "../components/LandingPageDescriptionBoxes/LandingPageDescriptionBoxes.tsx";
import { TitleElement } from "../components/TitleElement/TitleElement.tsx";
import { StandardPageHead } from "../components/Heads/StandardPageHead.tsx";

import { ChildVenue } from "../types/dataTypes.ts";
import { useFilmListingsController } from "../hooks/useFilmListingsController.ts";
import { FilmListingsSection } from "../components/FilmListingsSection/FilmListingsSection.tsx";
import { LoadingElementInBox } from "../components/Loading/LoadingElement.tsx";

type CityLandingPageProps = {
  locationText: string;
  availableVenuesText: string;
  slug: string;
  latitude: number;
  longitude: number;
  nearby_venues: ChildVenue[];
};

export const CityLandingPage: React.FC<CityLandingPageProps> = ({
  locationText,
  availableVenuesText,
  slug,
  nearby_venues,
}) => {
  const validVenueSlugs = React.useMemo(
    () => nearby_venues.map((v) => v.venue_slug),
    [nearby_venues],
  );

  const initialVenues = React.useMemo(
    () => nearby_venues.slice(0, 5).map((v) => v.venue_slug),
    [nearby_venues],
  );

  const controller = useFilmListingsController({
    contextSlug: slug,
    initialSelectedVenues: initialVenues,
    validVenueSlugs,
    defaultOptimise: false,
    enabled: validVenueSlugs.length > 0 && initialVenues.length > 0,
  });

  return (
    <>
      <CssBaseline />
      <StandardPageHead
        title={`Tickets & Showtimes for every Film Showing in ${locationText}`}
        description={`Browse movie showtimes from every cinema in ${locationText} on one page. ${availableVenuesText}`}
        pageUrl={`https://cinemaguideireland.com/${slug}`}
      />

      {controller.isInitialLoading ? (
        <LoadingElementInBox />
      ) : (
        <>
          <Box
            component="main"
            sx={{
              bgcolor: "background.default",
              color: "text.primary",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TitleElement
              headerOneText={`Plan your next cinema trip in ${locationText}.`}
              headerTwoText={`View every ${locationText} cinema showtime in one place.`}
            />

            <LandingPageDescriptionBoxes
              locationText={`${locationText}`}
              boxOneHeader={`${nearby_venues.length}+ ${locationText} Cinemas`}
              boxOneText={availableVenuesText}
              boxTwoHeader="Purchase Cinema Tickets"
              boxTwoText={`Buy tickets to watch any film showing in ${locationText}. Pay no extra fees. Book tickets fast.`}
              boxThreeHeader={`All ${locationText} Cinema Showtimes`}
              boxThreeText={`See every film showing in ${locationText}. Compare different showtimes. Get the cheapest cinema tickets.`}
            />

            <Box
              sx={{
                pt: { xs: 5, md: 3 },
                pb: { xs: 2, md: 2 },
                textAlign: "center",
              }}
            >
              {/* <Container>
                <Typography
                  variant="h5"
                  component="p"
                  gutterBottom
                  color="text.secondary"
                  sx={{
                    marginBottom: { xs: 4, md: 4 },
                    fontSize: { xs: "x-small", md: "small" },
                    px: { xs: 0, md: 10 },
                  }}
                >
                  {availableVenuesText}
                </Typography>
              </Container> */}

              <LinkToCinemaSelectPage
                leadText={`Not in ${locationText}?`}
                appendHref={false}
                fontSizeParam={{ xs: "small", md: "x-large" }}
              />
            </Box>
          </Box>

          <FilmListingsSection
            controller={controller}
            venuesNearLocation={nearby_venues}
            selectAllVenuesLabel={`SELECT ALL VENUES NEAR ${locationText.toUpperCase()}`}
            venueDistanceLabel="from city centre"
          />
        </>
      )}

      <noscript>
        <div>
          CinemaGuide requires JavaScript to display the latest showtimes.
          Please enable JavaScript in your browser.
        </div>
      </noscript>
    </>
  );
};
